import { createContext, useContext, useState, useEffect, useCallback, type ReactNode } from 'react';
import { authClient } from '../lib/auth-client';
import { authApi, type User, type LoginRequest, type RegisterRequest } from '../lib/api';
import { identifyUser, aliasUser, resetUser } from '../lib/analytics';

interface AuthContextType {
  user: User | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (data: LoginRequest) => Promise<void>;
  register: (data: RegisterRequest) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchLenderProfile = useCallback(async (): Promise<User | null> => {
    try {
      const userData = await authApi.me();
      return userData;
    } catch {
      return null;
    }
  }, []);

  // Check for existing session on mount
  useEffect(() => {
    const initAuth = async () => {
      try {
        // Check if there's an active Better Auth session
        const { data: session } = await authClient.getSession();
        if (session) {
          // Session exists, fetch the lender profile
          const lenderProfile = await fetchLenderProfile();
          setUser(lenderProfile);
          if (lenderProfile) identifyUser(lenderProfile.id, lenderProfile.email);
        }
      } catch {
        // No active session
      }
      setIsLoading(false);
    };

    initAuth();
  }, [fetchLenderProfile]);

  const login = async (data: LoginRequest) => {
    const { error } = await authClient.signIn.email({
      email: data.email,
      password: data.password,
    });

    if (error) {
      throw new Error(error.message || 'Invalid email or password');
    }

    // Fetch the lender profile after successful sign-in
    const lenderProfile = await fetchLenderProfile();
    if (!lenderProfile) {
      throw new Error('Failed to load user profile');
    }
    setUser(lenderProfile);
    identifyUser(lenderProfile.id, lenderProfile.email);
  };

  const register = async (data: RegisterRequest) => {
    const { error } = await authClient.signUp.email({
      name: `${data.firstName} ${data.lastName}`.trim(),
      email: data.email,
      password: data.password,
    });

    if (error) {
      throw new Error(error.message || 'Registration failed');
    }

    // The databaseHook on the server auto-creates the lender row.
    // If companyName was provided, update the profile immediately.
    if (data.companyName) {
      try {
        await authApi.updateProfile({ companyName: data.companyName });
      } catch {
        // Non-critical - companyName can be set later
      }
    }

    // Fetch the lender profile
    const lenderProfile = await fetchLenderProfile();
    if (!lenderProfile) {
      throw new Error('Failed to load user profile');
    }
    // alias links the anonymous email-keyed person to the authenticated user ID
    aliasUser(lenderProfile.id);
    identifyUser(lenderProfile.id, lenderProfile.email);
    setUser(lenderProfile);
  };

  const logout = async () => {
    try {
      await authClient.signOut();
    } catch {
      // Ignore logout errors
    }
    resetUser();
    setUser(null);
  };

  const refreshUser = async () => {
    try {
      const userData = await fetchLenderProfile();
      setUser(userData);
      if (userData) identifyUser(userData.id, userData.email);
    } catch (error) {
      console.error('Failed to refresh user:', error);
    }
  };

  return (
    <AuthContext.Provider
      value={{
        user,
        isLoading,
        isAuthenticated: !!user,
        login,
        register,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
